import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers, Users, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/lib/seo";
import { trackCTA, trackFormSubmit, trackFormStart } from "@/lib/analytics";
import { api, formatApiErrorDetail } from "@/lib/api";
import CTASection from "@/components/CTASection";

const SUPPORT = [
  "Back-office environmental reporting support",
  "Tier II preparation",
  "Chemical inventory data processing",
  "SDS review",
  "Regulatory data preparation",
  "Documentation support",
  "Recurring technical support",
  "Behind-the-scenes / white-label delivery where appropriate",
];

const WHO = [
  "U.S. environmental consulting firms",
  "U.S. EHS consulting firms",
  "Engineering firms",
  "Compliance firms",
  "Independent environmental consultants",
];

export default function ForConsultants() {
  useSEO({
    title: "White-Label Tier II Reporting Support for Environmental Consultants",
    description:
      "Expand your EHS capacity without hiring more staff. Wasserlab — EHS Remote provides white-label, back-office Tier II and chemical inventory reporting support for U.S. environmental, EHS, engineering and compliance firms.",
    path: "/for-consultants",
  });

  const [form, setForm] = useState({
    company: "", first_name: "", email: "", num_facilities: "", workload: "", message: "", company_website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const composed = `Number of facilities: ${form.num_facilities || "—"}. Current reporting workload: ${form.workload || "—"}. ${form.message || ""}`.trim();
      await api.post("/leads/contact", {
        first_name: form.first_name,
        email: form.email,
        company: form.company,
        service_interest: "Consulting Partner",
        message: composed,
        company_website: form.company_website,
      });
      trackFormSubmit("partner");
      setDone(true);
      document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">For Consultants &amp; Firms</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Expand Your EHS Capacity Without Hiring More Staff
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            White-label Tier II reporting support for environmental consultants. You manage the client relationship —
            we support the reporting workload, delivered remotely by our EHS team.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => { trackCTA("Become a Partner", "consultants-hero"); scrollToForm(); }}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-7 py-3.5 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
              data-testid="consultants-cta-partner"
            >
              Become a Consulting Partner <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
            <Link
              to="/free-tier-ii-assessment"
              onClick={() => trackCTA("Free Assessment", "consultants-hero")}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
              data-testid="consultants-cta-assessment"
            >
              Get a Free Tier II Assessment
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-2xl font-bold text-navy-900">How we support your firm</h2>
            <ul className="mt-6 space-y-3">
              {SUPPORT.map((s, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`consultant-support-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                  <span className="text-base leading-relaxed text-slate-700">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-navy-900">Who this is for</h2>
            <ul className="mt-6 space-y-3">
              {WHO.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Users className="mt-0.5 h-5 w-5 shrink-0 text-navy-900" aria-hidden="true" />
                  <span className="text-base leading-relaxed text-slate-700">{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Layers, t: "Scales with you", d: "Add capacity per project or season without adding headcount." },
                { icon: CalendarClock, t: "Recurring cycles", d: "Ongoing support for annual reporting seasons." },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="rounded-xl border border-slate-200 p-5">
                    <Icon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                    <h3 className="mt-2 font-heading text-sm font-bold text-navy-900">{c.t}</h3>
                    <p className="mt-1 text-sm text-slate-600">{c.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-5 lg:px-8 mt-12">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm leading-relaxed text-slate-700">
              We provide technical preparation and back-office support. We do not provide licensed professional
              engineering opinions or legal/regulatory determinations that require a license — those remain with your
              firm and its credentialed professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Partner lead form */}
      <section id="partner" className="bg-slate-50 py-16 lg:py-20 scroll-mt-24" data-testid="partner-section">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Become a Consulting Partner</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy-900">Tell us about your reporting workload</h2>
            <p className="mt-3 text-base text-slate-600">We'll review your needs and follow up to discuss a scoped, confidential engagement.</p>
          </div>

          {done ? (
            <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="partner-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden="true" />
              <h3 className="mt-4 font-heading text-2xl font-bold text-navy-900">Thank You — Your Request Has Been Received</h3>
              <p className="mt-2 text-slate-600">Our team will review your information and contact you to discuss how we can support your firm.</p>
              <Link to="/services/tier-ii-reporting-support" className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" data-testid="partner-learn-more">
                Learn about our Tier II support <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} onFocus={() => { if (!started) { setStarted(true); trackFormStart("partner"); } }} className="mt-8 rounded-xl border border-slate-200 bg-white p-6 lg:p-8 space-y-5" data-testid="partner-form">
              <input type="text" value={form.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="p-company">Company *</Label>
                  <Input id="p-company" required value={form.company} onChange={set("company")} className="mt-1.5" data-testid="partner-company" />
                </div>
                <div>
                  <Label htmlFor="p-contact">Contact Name *</Label>
                  <Input id="p-contact" required value={form.first_name} onChange={set("first_name")} className="mt-1.5" data-testid="partner-contact" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="p-email">Work Email *</Label>
                  <Input id="p-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" data-testid="partner-email" />
                </div>
                <div>
                  <Label htmlFor="p-fac">Number of Facilities</Label>
                  <Input id="p-fac" value={form.num_facilities} onChange={set("num_facilities")} className="mt-1.5" data-testid="partner-facilities" />
                </div>
              </div>
              <div>
                <Label htmlFor="p-workload">Current Reporting Workload</Label>
                <Input id="p-workload" value={form.workload} onChange={set("workload")} className="mt-1.5" placeholder="e.g. ~20 Tier II reports per season" data-testid="partner-workload" />
              </div>
              <div>
                <Label htmlFor="p-msg">Message</Label>
                <Textarea id="p-msg" rows={4} value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Tell us where your team needs support." data-testid="partner-message" />
              </div>
              <p className="text-xs text-slate-500 text-center">Your information is kept confidential and used only to respond to your request.</p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-6 py-3.5 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-60"
                data-testid="partner-submit"
              >
                {submitting && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Become a Consulting Partner
              </button>
            </form>
          )}
        </div>
      </section>

      <CTASection
        heading="Let's talk about your capacity needs"
        sub="Tell us where your team is stretched — we'll propose a scoped, confidential engagement."
        location="consultants-footer"
      />
    </>
  );
}
